import { Context } from "../Engine";
import { Scene } from "../Scene";
import { RenderMode, IDrawableObject } from "../Object/ObjectManager";
import { Camera } from "../View/Camera";
import { SceneRenderer } from "./SceneRenderer";

export class PickingSceneRenderer extends SceneRenderer {
    
    private buffer: WebGLFramebuffer;
    private ctx: Context;

    public init(ctx: Context) {
        this.ctx = ctx;

        // https://stackoverflow.com/questions/29578535/webgl-binding-of-a-framebuffer-and-renderbuffer?rq=1
        // https://stackoverflow.com/questions/21841483/webgl-using-framebuffers-for-picking-multiple-objects

        let gl = ctx.gl;
        let width = this.ctx.viewPort.width;
        let height = this.ctx.viewPort.height;

        let framebuffer = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);

        let depthBuffer = gl.createRenderbuffer();
        gl.bindRenderbuffer(gl.RENDERBUFFER, depthBuffer);

        // allocate renderbuffer
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, width, height);

        // attach renderebuffer
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, depthBuffer);

        let colorBuffer = gl.createRenderbuffer();
        gl.bindRenderbuffer(gl.RENDERBUFFER, colorBuffer);
        // allocate colorBuffer
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.RGBA4, width, height);

        // attach colorbuffer
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, colorBuffer);

        if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
            throw new Error("this combination of attachments does not work");
        }

        this.buffer = framebuffer;
    }

    public destroy(): void {
        // TODO: implement
    }

    public render(scene: Scene, camera: Camera, buffer: WebGLFramebuffer) {
        this.renderImpl(scene, camera, buffer, 0, 0);
    }

    public pick(scene: Scene, camera: Camera, x: number, y: number): IDrawableObject {
        return this.renderImpl(scene, camera, this.buffer, x, y);
    }

    private renderImpl(scene: Scene, camera: Camera, buffer: WebGLFramebuffer, x: number, y: number): IDrawableObject {
        let ctx = this.ctx;
        let gl = ctx.gl;

        let viewPort = this.ctx.viewPort;

        // Bind the picking buffer so everything gets drawn to that instead of the canvas
        gl.bindFramebuffer(gl.FRAMEBUFFER, buffer);

        // Setup the viewport and clear the screen
        gl.viewport(0, 0, viewPort.width, viewPort.height);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        let map = new Map<number, IDrawableObject>();
        let idx = 1;
        // Draw all pickable objects
        scene.objectManager.iterate(RenderMode.Picking, obj => {
            map.set(idx, obj);
            obj.render(ctx, camera, { mode: RenderMode.Picking, pickingReference: idx });
            idx++;
        });

        // Have this function return the picked color
        let colorPicked = new Uint8Array(4);
        gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, colorPicked);
        let pickedIdx = colorPicked[0] | (colorPicked[1] << 8) | (colorPicked[2] << 16);

        if (map.has(pickedIdx)) {
            return map.get(pickedIdx);
        } else {
            return null;
        }
    }
}